/**
 * In-app help (client layer `help`): one Markdown page per screen or task, in `help/*.md` with front matter, read
 * by the sync step into `help.pages`. `help/whats-new.md` also gives `help.whatsNew`, its dated entries. The
 * platform supplies no wording: the pages are the client's own.
 *
 * Front matter (one `key: value` per line, values optionally quoted):
 *   id        unique, lower case, e.g. "jobs"
 *   title     "Jobs"
 *   route     the portal screen it explains ("#/jobs"), or a web address for something outside the portal
 *   audience  office, crew or all (crew users see crew and all pages)
 *   summary   one line, shown in lists and empty states
 *   updated   YYYY-MM-DD
 *   platform  the platform version it was written against, e.g. "v0.8.5"
 *
 * Also `support`: where "Something's not working" reports go.
 */
import { z } from "zod";

/** Portal screens a help page can belong to. */
export const HELP_ROUTES = [
  "#/today",
  "#/my",
  "#/leads",
  "#/clients",
  "#/quotes",
  "#/jobs",
  "#/bookings",
  "#/crew",
  "#/equipment",
  "#/incidents",
  "#/crm",
  "#/rates",
  "#/terms",
  "#/integrations",
  "#/invoices",
  "#/pay",
  "#/help",
] as const;

export const HELP_AUDIENCES = ["office", "crew", "all"] as const;
export type HelpAudience = (typeof HELP_AUDIENCES)[number];

const text = z.string().trim().min(1);

export const helpPageSchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,59}$/, "Expected a lower case id such as \"jobs\""),
  title: text.max(120),
  route: z.string().refine((r) => (HELP_ROUTES as readonly string[]).includes(r) || /^https:\/\/[^\s]+$/.test(r), (r) => ({ message: `Unknown route "${r}". Use one of ${HELP_ROUTES.join(", ")} or a web address.` })),
  audience: z.enum(HELP_AUDIENCES),
  summary: text.max(300),
  updated: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD"),
  platform: z.string().regex(/^v\d+\.\d+\.\d+$/, "Expected a version such as v0.8.5").optional(),
  /** The page's Markdown, after the front matter. */
  body: z.string(),
});
export type HelpPage = z.output<typeof helpPageSchema>;

export const whatsNewEntrySchema = z.object({
  /**
   * YYYY-MM-DD, from the heading's date, or "" for a release not out yet: a heading of just the version
   * ("v0.8.15") until it reaches users, when the date goes in ("4 December 2026 (v0.8.15)"). Newest first.
   */
  date: z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/),
  /** The heading as written, e.g. "31 October 2026 (v0.8.5)". */
  title: text,
  body: z.string(),
});
export type WhatsNewEntry = z.output<typeof whatsNewEntrySchema>;

export const helpSchema = z
  .object({
    pages: z.array(helpPageSchema).default([]),
    whatsNew: z.array(whatsNewEntrySchema).default([]),
  })
  .superRefine((h, ctx) => {
    const seen = new Set<string>();
    h.pages.forEach((p, i) => {
      if (seen.has(p.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["pages", i, "id"], message: `Help page id "${p.id}" is used twice` });
      seen.add(p.id);
    });
    for (let i = 1; i < h.whatsNew.length; i++) {
      const [prev, cur] = [h.whatsNew[i - 1]!, h.whatsNew[i]!];
      if (!cur.date && prev.date) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["whatsNew", i], message: "Entries not out yet (no date) go above the dated ones" });
      else if (cur.date && prev.date && cur.date > prev.date) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["whatsNew", i], message: "What's new entries go newest first" });
    }
  });
export type HelpConfig = z.output<typeof helpSchema>;

export const supportSchema = z.object({
  /** Where "Something's not working" reports are emailed. */
  email: z.string().email(),
  /** Who looks after the app, as in "Thanks, {team} has your report." */
  team: text.max(60),
  confirmation: text.refine((s) => !s.includes("—"), "No em dashes in client-facing text").default("Thanks, {team} has your report."),
});
export type SupportConfig = z.output<typeof supportSchema>;

/* ------------------------------------------------------------------ parsing (used by the sync step) */

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

/** Split a help file into its front matter and body. Throws with the file name on a malformed file. */
export function parseHelpFile(name: string, src: string): Record<string, string> & { body: string } {
  const s = src.replace(/\r\n?/g, "\n").replace(/^﻿/, "");
  const m = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/.exec(s);
  if (!m) throw new Error(`help/${name}: expected front matter between --- lines at the top`);
  const meta: Record<string, string> = {};
  for (const line of m[1]!.split("\n")) {
    if (!line.trim() || line.trim().startsWith("#")) continue;
    const kv = /^([a-zA-Z]+):\s*(.*)$/.exec(line);
    if (!kv) throw new Error(`help/${name}: cannot read front matter line "${line}"`);
    let v = kv[2]!.trim();
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) v = v.slice(1, -1);
    meta[kv[1]!] = v;
  }
  return { ...meta, body: m[2]!.trim() };
}

/** "4 November 2026" or "31 October 2026 (v0.8.5)" to "2026-11-04". */
export function headingDate(heading: string): string | null {
  const m = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})\b/.exec(heading.trim());
  if (!m) return null;
  const month = MONTHS.indexOf(m[2]!.toLowerCase());
  if (month < 0) return null;
  return `${m[3]}-${String(month + 1).padStart(2, "0")}-${m[1]!.padStart(2, "0")}`;
}

/** What's new entries: each "## <date>" heading and the text under it. */
export function parseWhatsNew(body: string): { date: string; title: string; body: string }[] {
  const out: { date: string; title: string; body: string }[] = [];
  let cur: { date: string; title: string; lines: string[] } | null = null;
  for (const line of body.split("\n")) {
    const h = /^##\s+(.+)$/.exec(line.trim());
    if (h) {
      if (cur) out.push({ date: cur.date, title: cur.title, body: cur.lines.join("\n").trim() });
      // A release not out yet has no date: its heading is just the version.
      const date = /^v\d+\.\d+\.\d+\b/.test(h[1]!.trim()) ? "" : headingDate(h[1]!);
      if (date === null) throw new Error(`help/whats-new.md: heading "${h[1]}" should start with a date such as "4 November 2026", or be just the version ("v0.8.15") until it is released`);
      cur = { date, title: h[1]!.trim(), lines: [] };
    } else if (cur) cur.lines.push(line);
  }
  if (cur) out.push({ date: cur.date, title: cur.title, body: cur.lines.join("\n").trim() });
  return out;
}

/** The pages an audience may see: crew users see crew and all pages; office users see everything. */
export const pagesFor = <T extends { audience: HelpAudience }>(pages: readonly T[], who: "office" | "crew"): T[] =>
  pages.filter((p) => (who === "crew" ? p.audience !== "office" : true));

/** How a What's new entry is remembered as seen: its date and heading (two entries can share a date). */
export const entryKey = (e: { date: string; title: string }) => `${e.date} ${e.title}`;

/** Entries newer than the one last seen: by key, or by date if that entry has since been renamed or removed. */
export function unseenSince<T extends { date: string; title: string }>(entries: readonly T[], seen: string | null): T[] {
  if (!seen) return [...entries];
  let i = entries.findIndex((e) => entryKey(e) === seen);
  // The same release, since dated ("v0.8.15" became "4 December 2026 (v0.8.15)"): still seen.
  const version = /v\d+\.\d+\.\d+/.exec(seen)?.[0];
  if (i < 0 && version) i = entries.findIndex((e) => e.title.includes(version));
  return i >= 0 ? entries.slice(0, i) : entries.filter((e) => !e.date || e.date > seen.slice(0, 10));
}
